import { MapContainer, TileLayer, Marker, Popup, Polyline, AttributionControl } from 'react-leaflet'
import { useTranslation } from 'react-i18next'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { isUnconfigured } from '../utils/device'
import { timeAgo } from '../utils/events'

function makePin(color) {
  return L.divIcon({
    html: '<div style="position:relative"><div style="width:14px;height:14px;border-radius:50%;background:' + color + ';border:2.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.25);position:relative;z-index:1"></div><div style="position:absolute;width:28px;height:28px;border-radius:50%;background:' + color + ';opacity:0.15;top:-7px;left:-7px"></div></div>',
    className: '', iconSize: [14, 14], iconAnchor: [7, 7], popupAnchor: [0, -10]
  });
}

const pins = {
  green: makePin('#14B8A6'),
  amber: makePin('#F59E0B'),
  red: makePin('#DC2626'),
  gray: makePin('#99A1AE')
};

// A truck row is considered "has a GPS fix" only if its position is non-zero.
// A 0/0 reading is what the parser writes before the first NMEA sentence
// arrives, and rendering it puts the map in the Gulf of Guinea.
function hasFix(t) {
  return t && Math.abs(Number(t.latitude || 0)) > 0.0001 && Math.abs(Number(t.longitude || 0)) > 0.0001;
}

// Default centre is Frankfurt-ish \u2014 sensible for a German fleet product so
// the map shows the home country instead of the ocean while waiting for GPS.
const FALLBACK_CENTER = [49.0, 10.0];
const FALLBACK_ZOOM = 6;

// The most recent event for a truck (events arrive newest-first from the API,
// but we don't rely on order — pick the max ts). Used for the pin card's
// "last event" line.
function lastEventFor(events, truckName) {
  let best = null;
  for (const e of events || []) {
    if (e.truck !== truckName) continue;
    if (!best || e.ts > best.ts) best = e;
  }
  return best;
}

const EVENT_LABEL_KEY = {
  theft: 'alerts.theft', tamper: 'alerts.tamper', refuel: 'dashboard.refuel',
  after_hours: 'alerts.afterHours', geofence_exit: 'alerts.geofence', geofence_enter: 'alerts.geofence',
};

export default function FleetMap({ trucks, selected, onSelect, track, dieselPrice = 1.14, events = [] }) {
  const { t } = useTranslation();
  const trucksWithFix = (trucks || []).filter(hasFix);
  const first = trucksWithFix.find(t => t.online) || trucksWithFix[0];
  const center = first ? [first.latitude, first.longitude] : FALLBACK_CENTER;
  const zoom = first ? 11 : FALLBACK_ZOOM;
  const noFixes = trucksWithFix.length === 0;

  return (
    <div style={{ height: '100%', position: 'relative' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--tg-border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 'var(--tg-weight-medium)', color: 'var(--tg-text-primary)' }}>Live map</span>
        <span style={{ fontSize: 10, color: 'var(--tg-text-muted)' }}>{trucks.filter(t => t.online).length} online</span>
      </div>
      <MapContainer center={center} zoom={zoom} attributionControl={false} style={{ height: 'calc(100% - 37px)', width: '100%' }}>
        <AttributionControl position="bottomleft" prefix={false} />
        <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {track && track.length > 1 && (
          <Polyline positions={track} color="#14B8A6" weight={2} opacity={0.6} dashArray="4 4" />
        )}
        {trucksWithFix.map(truck => {
          const unconfigured = isUnconfigured(truck);
          const icon = unconfigured || !truck.online ? pins.gray : truck.hasAlert ? pins.red : truck.fuel_liters < 50 ? pins.amber : pins.green;
          const maxL = (Number(truck.tank1_max_liters) || 0) + (Number(truck.tank2_max_liters) || 0);
          const pct = maxL > 0 ? Math.round((truck.fuel_liters / maxL) * 100) : null;
          const eur = Math.round((truck.fuel_liters || 0) * dieselPrice);
          const status = truck.hasAlert
            ? { label: t('dashboard.alert'), color: 'var(--tg-danger-600, #dc2626)' }
            : truck.online
              ? { label: t('fleet.online'), color: 'var(--tg-success-600, #16a34a)' }
              : { label: t('fleet.offline'), color: 'var(--tg-graphite-400, #99a1ae)' };
          const ev = lastEventFor(events, truck.name);
          return (
            <Marker key={truck.id} position={[truck.latitude, truck.longitude]} icon={icon}
              eventHandlers={{ click: () => onSelect(truck.id) }}>
              <Popup>
                <div className="map-pin-card">
                  <div className="map-pin-card__head">
                    <span className="map-pin-card__name">{truck.name}</span>
                    <span className="map-pin-card__status" style={{ color: status.color }}>
                      <span className="map-pin-card__dot" style={{ background: status.color }} />{status.label}
                    </span>
                  </div>
                  <div className="map-pin-card__rows">
                    <div><span>{t('fleet.fuel')}</span><b>{truck.fuel_liters.toFixed(0)} L{pct != null ? ' · ' + pct + '%' : ''}</b></div>
                    <div><span>{t('dashboard.value')}</span><b>€{eur.toLocaleString()}</b></div>
                    <div><span>{t('dashboard.lastSeen')}</span><b>{truck.last_seen ? timeAgo(truck.last_seen) : t('fleet.never')}</b></div>
                    {ev && (
                      <div><span>{t('dashboard.lastEvent')}</span><b>{t(EVENT_LABEL_KEY[ev.type] || 'alerts.anomaly')} · {timeAgo(ev.ts)}</b></div>
                    )}
                  </div>
                  <button className="map-pin-card__btn" onClick={() => onSelect(truck.id)}>{t('dashboard.viewDetails')}</button>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
      {noFixes && trucks.length > 0 && (
        <div style={{ position: 'absolute', top: 'calc(50% + 18px)', left: '50%', transform: 'translate(-50%, -50%)', background: 'rgba(255,255,255,0.94)', border: '1px solid var(--tg-border-default)', borderRadius: 'var(--tg-radius-lg)', padding: '12px 16px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', textAlign: 'center', pointerEvents: 'none' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tg-text-primary)' }}>No GPS fix yet</div>
          <div style={{ fontSize: 11, color: 'var(--tg-text-secondary)', marginTop: 4 }}>Waiting for first telemetry from your fleet.</div>
        </div>
      )}
    </div>
  );
}
